import { useEffect, useState } from 'react';
import { Card, CardBody, Col, Input, Table } from 'reactstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { searchRole, getRoleById } from '../../../store/apis/adminService';
import './Role.scss';
import { CreateRole, DeleteRole, EditRole } from '../../../components/Admin';
import editIcon from '../../../assets/icons/editblueicon.png';

function Role() {
  document.title = 'Roles | Envision Daikin';

  const queryClient = useQueryClient();

  // State for search and filter
  const [roleName, setRoleName] = useState('');
  const [debouncedRoleName, setDebouncedRoleName] = useState('');
  const [isActive, setActive] = useState(true);
  const [editRoleId, setEditRoleId] = useState(null);
  const [selectedRole, setSelectedRole] = useState();
  const [editRoleModal, setEditRoleModal] = useState(false);
  const [deleteRoleModal, setDeleteRoleModal] = useState(false);
  const [createRoleModal, setCreateRoleModal] = useState(false);

  // Debounce effect for roleName
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedRoleName(roleName);
    }, 400); // 400ms debounce

    return () => clearTimeout(handler);
  }, [roleName]);

  // Fetch roles with search and status as query keys
  const { data: roles, isLoading } = useQuery({
    queryKey: ['roles', { debouncedRoleName, isActive }],
    queryFn: () => searchRole({ roleName, isActive }),
    keepPreviousData: true,
  });

  // Fetch role details by ID when editRoleId is set
  const { data: editRoleDetails, isLoading: isLoadingEditRole } = useQuery({
    queryKey: ['roleById', editRoleId],
    queryFn: () => getRoleById(editRoleId),
    enabled: !!editRoleId, // Only fetch when editRoleId is set
  });

  const toggleCreateRoleModal = () => {
    setCreateRoleModal(!createRoleModal);
  };

  const toggleUpdateRoleModal = () => {
    setEditRoleModal(!editRoleModal);
    setEditRoleId(null);
    queryClient.invalidateQueries({ queryKey: ['roleById'] });
  };

  const toggleDeleteRoleModal = () => {
    setDeleteRoleModal(!deleteRoleModal);
  };

  const toggleRole = newRole => {
    setSelectedRole(newRole);
  };

  const handleEditRole = role => {
    // Open edit modal or navigate to edit page with role data
    setEditRoleId(role.ID);
    setEditRoleModal(true);
  };

  const handleRoleUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['roles'] });
  };

  // Render roles from API
  const renderRoles = () =>
    roles?.map((role, index) => (
      <tr
        className="cursor-pointer"
        key={role.ID}
        onClick={() => toggleRole(role.roleName)}
      >
        <td style={{ width: '50px' }}>{index + 1}.</td>
        <td>{role.Name}</td>
        <td>{role.Status}</td>
        <td className="text-center" style={{ width: '100px' }}>
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={() => handleEditRole(role)}
            title="Edit"
          >
            <img src={editIcon} alt="Edit User" style={{ height: '15px' }} />
          </button>
        </td>
      </tr>
    ));

  // Render table body without nested ternary
  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={2}>Loading...</td>
        </tr>
      );
    }
    if (Array.isArray(roles) && roles.length > 0) {
      return renderRoles();
    }
    return (
      <tr>
        <td colSpan={2}>No roles found</td>
      </tr>
    );
  };

  return (
    <div className="page-content role-page">
      <Card className="role-card">
        <CardBody className="p-0">
          <div className="role-header px-3 py-2">
            <div className="d-flex flex-row align-items-center gap-2">
              <div>
                <span
                  className="fs-5"
                  style={{ color: '#000', fontWeight: '600' }}
                >
                  Roles
                </span>
              </div>
              <div className="role-count">{roles?.length ?? 0}</div>
            </div>
            <button
              className="custom-btn fs-6"
              type="button"
              onClick={toggleCreateRoleModal}
            >
              ADD
            </button>
          </div>
          <div className="external-filter gap-2 mx-3 mb-3 p-3">
            <Col className="external-filter-col">
              <b>Role</b>
              <Input
                type="text"
                placeholder="Search Role"
                className="form-control external-filter-input"
                value={roleName}
                onChange={e => setRoleName(e.target.value)}
              />
            </Col>
            <Col className="external-filter-col">
              <b>Status</b>
              <Input
                type="select"
                className="form-select external-filter-input"
                value={isActive}
                onChange={e => setActive(e.target.value)}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Input>
            </Col>
            <Col>
              <div className="external-filter-btns gap-2">
                <button
                  className="custom-btn"
                  type="button"
                  onClick={() => {
                    setRoleName('');
                    setActive(true);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="custom-btn"
                  onClick={() => {
                    setRoleName(debouncedRoleName);
                    setActive(isActive);
                  }}
                >
                  Search
                </button>
              </div>
            </Col>
          </div>
          <div className="d-flex flex-row">
            {/* List of Roles */}
            <Col>
              <div className="table-responsive role-table-container">
                <Table className="custom-table role-table mb-0">
                  <thead>
                    <tr>
                      <th className="fw-semibold">#</th>
                      <th className="fw-semibold">Role</th>
                      <th className="fw-semibold">Status</th>
                      <th className="fw-semibold text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>{renderTableBody()}</tbody>
                </Table>
              </div>
            </Col>
          </div>
        </CardBody>
      </Card>
      <DeleteRole
        isOpen={deleteRoleModal}
        toggle={toggleDeleteRoleModal}
        role={selectedRole}
      />
      <CreateRole
        isOpen={createRoleModal}
        toggle={toggleCreateRoleModal}
        handleRoleUpdated={handleRoleUpdated}
      />
      <EditRole
        isOpen={editRoleModal}
        toggle={toggleUpdateRoleModal}
        roleDetails={editRoleDetails}
        isLoading={isLoadingEditRole}
        handleRoleUpdated={handleRoleUpdated}
      />
    </div>
  );
}

export default Role;
